import { type CalendarDate, parseDate, today } from '@internationalized/date';
import type { CalendarState } from '@react-stately/calendar';
import { addYears, format, formatISO, isBefore, isValid, parse, startOfDay } from 'date-fns';
import { cs, de, enGB, enUS, es, fr, it, ja, pt, ptBR, zhCN } from 'date-fns/locale';

import {
  isAfterMax,
  isBeforeMin,
  isDisabledByFuturePreset,
  isDisabledByPastPreset,
  isDisabledByWeekdayPresets,
  isInDisabledDates,
  isInDisabledRanges,
  isMonthDisabledByTimePreset,
  isMonthRangeFullyDisabled,
  isYearDisabledByTimePreset,
  isYearRangeFullyDisabled,
} from './dateDisabling';
import type { DisablePresets } from './types';

export type dataAttribute = 'data-month' | 'data-year';

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

const DATE_FNS_LOCALES = {
  'en-US': enUS,
  'en-GB': enGB,
  'fr-FR': fr,
  'de-DE': de,
  'es-ES': es,
  'it-IT': it,
  'pt-PT': pt,
  'pt-BR': ptBR,
  'cs-CZ': cs,
  'zh-CN': zhCN,
  'ja-JP': ja,
} as const;

export const LOCALE_DATE_FORMATS = {
  'en-US': 'MM/dd/yyyy',
  'en-GB': 'dd/MM/yyyy',
  'fr-FR': 'dd/MM/yyyy',
  'de-DE': 'dd.MM.yyyy',
  'es-ES': 'dd/MM/yyyy',
  'it-IT': 'dd/MM/yyyy',
  'pt-PT': 'dd/MM/yyyy',
  'pt-BR': 'dd/MM/yyyy',
  'cs-CZ': 'dd.MM.yyyy',
  'zh-CN': 'yyyy/MM/dd',
  'ja-JP': 'yyyy/MM/dd',
} as const;

export function createIsDateUnavailable(presets?: DisablePresets, timeZone = 'UTC'): (date: CalendarDate) => boolean {
  return (date: CalendarDate) => {
    if (!presets) {
      return false;
    }

    const todayDate = today(timeZone);

    // Check if date is in the future
    if (presets.future && isDisabledByFuturePreset(date, todayDate)) {
      return true;
    }

    // Check if date is in the past
    if (presets.past && isDisabledByPastPreset(date, todayDate)) {
      return true;
    }

    // Check weekdays (weekends, mondays, tuesdays, etc.)
    if (isDisabledByWeekdayPresets(date, presets)) {
      return true;
    }

    // Check specific dates
    if (presets.dates && isInDisabledDates(date, presets.dates)) {
      return true;
    }

    // Check date ranges
    return Boolean(presets.ranges && isInDisabledRanges(date, presets.ranges));
  };
}

export function getDefaultDateFormat(locale: string): string {
  return LOCALE_DATE_FORMATS[locale as keyof typeof LOCALE_DATE_FORMATS] ?? DEFAULT_DATE_FORMAT;
}

export const formatValueForInput = (date: CalendarDate | null, locale: string, dateFormat?: string): string => {
  if (!date) {
    return '';
  }

  return formatDateForLocale(date, locale, dateFormat);
};

export function formatDateForLocale(date: CalendarDate, locale: string, dateFormat?: string): string {
  const jsDate = new Date(date.year, date.month - 1, date.day);

  const effectiveFormat = dateFormat ?? getDefaultDateFormat(locale);

  const dateFnsLocale = DATE_FNS_LOCALES[locale as keyof typeof DATE_FNS_LOCALES] ?? enGB;

  return format(jsDate, effectiveFormat, { locale: dateFnsLocale });
}

/**
 * Check if locale uses US date format (MM/DD/YYYY)
 */
function isUsLocale(locale: string): boolean {
  return locale.startsWith('en-US');
}

/**
 * Parse abbreviated date format (6 digits without separators)
 * - US locales (en-US): MMDDYY
 * - EU locales (others): DDMMYY
 *
 * Note: no year inference is applied here — the user explicitly provided the year digits.
 */
function parseAbbreviatedDate(dateString: string, locale: string): Date | null {
  const trimmed = dateString.trim();

  // Check if input is exactly 6 digits
  if (!/^\d{6}$/.test(trimmed)) {
    return null;
  }

  let day: number;
  let month: number;
  let year: number;

  const isUs = isUsLocale(locale);

  if (isUs) {
    // US format: MMDDYY
    month = Number.parseInt(trimmed.substring(0, 2), 10);
    day = Number.parseInt(trimmed.substring(2, 4), 10);
    const yy = Number.parseInt(trimmed.substring(4, 6), 10);

    year = 2000 + yy; // Always assume 2000s for US format
  } else {
    // EU format: DDMMYY
    day = Number.parseInt(trimmed.substring(0, 2), 10);
    month = Number.parseInt(trimmed.substring(2, 4), 10);
    const yy = Number.parseInt(trimmed.substring(4, 6), 10);

    year = 2000 + yy; // Always assume 2000s for EU format
  }

  // Validate month and day ranges
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  // Validate that the date is valid (e.g., not 31st of February)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

/**
 * When a date is parsed from a no-year format and falls before today, advance it by one year.
 * This mirrors the FX behaviour: typing "0404" on May 12 2026 yields April 4 2027,
 * not April 4 2026.
 */
function advanceToNextYearIfPast(date: Date): Date {
  if (isBefore(startOfDay(date), startOfDay(new Date()))) {
    return addYears(date, 1);
  }

  return date;
}

export function parseDateForLocale(
  dateString: string,
  locale: string,
  dateInputCultureInfo?: string,
): CalendarDate | null {
  if (!dateString || dateString.trim() === '') {
    return null;
  }

  // Format ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return parseDate(dateString);
  }

  // Try abbreviated format first (6 digits without separators)
  // No year inference: the user explicitly provided the year digits.
  const abbreviatedDate = parseAbbreviatedDate(dateString, dateInputCultureInfo ?? locale);

  if (abbreviatedDate && isValid(abbreviatedDate)) {
    const isoString = formatISO(abbreviatedDate, { representation: 'date' });
    return parseDate(isoString);
  }

  const dateFnsLocale = DATE_FNS_LOCALES[dateInputCultureInfo as keyof typeof DATE_FNS_LOCALES] ?? enGB;

  const localeDefaultFormat = getDefaultDateFormat(dateInputCultureInfo ?? locale);
  const shortYearFormats = isUsLocale(dateInputCultureInfo ?? locale)
    ? ['MM/dd/yy', 'MM dd yy', 'MMddyy']
    : ['dd/MM/yy', 'dd MM yy', 'ddMMyy'];
  const noYearFormats = isUsLocale(dateInputCultureInfo ?? locale)
    ? ['MM/dd', 'MM dd', 'MMdd']
    : ['dd/MM', 'dd MM', 'ddMM'];

  const formatsToTry = [
    // ...(dateFormat ? [dateFormat] : []),
    localeDefaultFormat,
    ...shortYearFormats,
    'dd/MM/yyyy',
    'MM/dd/yyyy',
    'd/M/yyyy',
    'M/d/yyyy',
    'dd-MM-yyyy',
    'MM-dd-yyyy',
    'd-M-yyyy',
    'M-d-yyyy',
    'yyyy-MM-dd',
    'dd.MM.yyyy',
    'd.M.yyyy',
    'dd MMM yyyy',
    'MMM dd, yyyy',
    'dd MMMM yyyy',
    'MMMM dd, yyyy',
    'd MMM yyyy',
    'MMM d, yyyy',
    'ddMMyyyy',
    'MMddyyyy',
    ...noYearFormats,
  ];

  const uniqueFormats = [...new Set(formatsToTry)];

  let jsDate: Date | undefined;
  let matchedFormat: string | undefined;

  for (const formatStr of uniqueFormats) {
    try {
      const parsedDate = parse(dateString, formatStr, new Date(), { locale: dateFnsLocale });
      const year = parsedDate.getFullYear();

      if (isValid(parsedDate) && year >= 1900 && year <= 2200) {
        jsDate = parsedDate;
        matchedFormat = formatStr;
        break;
      }
    } catch {}
  }

  if (!jsDate || !isValid(jsDate)) {
    // console.warn('❌ Failed to parse date with any format:', dateString);
    // FIXME: this is caught just below
    return null;
  }

  // If the input was a no-year format and the resulting date is in the past,
  // advance by one year to match FX behaviour (e.g. "0404" on May 12 → April 4 next year).
  const effectiveDate =
    matchedFormat !== undefined && noYearFormats.includes(matchedFormat) ? advanceToNextYearIfPast(jsDate) : jsDate;

  const isoString = formatISO(effectiveDate, { representation: 'date' });

  return parseDate(isoString);
}

/**
 * Check if a month should be disabled
 */
export function isMonthDisabled(
  month: number,
  currentYear: number,
  state: CalendarState,
  disablePresets: DisablePresets | undefined,
): boolean {
  if (!disablePresets) {
    return false;
  }

  const todayDate = today(state.timeZone);
  const minValue = state.minValue;
  const maxValue = state.maxValue;

  // Check minValue
  if (minValue) {
    const firstDay = state.focusedDate.set({ year: currentYear, month, day: 1 });
    if (isBeforeMin(firstDay, minValue)) {
      return true;
    }
  }

  // Check maxValue
  if (maxValue) {
    const daysInMonth = new Date(currentYear, month, 0).getDate();
    const lastDay = state.focusedDate.set({ year: currentYear, month, day: daysInMonth });
    if (isAfterMax(lastDay, maxValue)) {
      return true;
    }
  }

  // Check disabled ranges
  if (disablePresets.ranges && isMonthRangeFullyDisabled(month, currentYear, disablePresets.ranges, state)) {
    return true;
  }

  // Check future/past presets
  if (disablePresets.future || disablePresets.past) {
    return isMonthDisabledByTimePreset(month, currentYear, todayDate.month, todayDate.year, disablePresets);
  }

  return false;
}

/**
 * Check if a year should be disabled
 */
export function isYearDisabled(
  year: number,
  state: CalendarState,
  disablePresets: DisablePresets | undefined,
): boolean {
  if (!disablePresets) {
    return false;
  }

  const todayDate = today(state.timeZone);
  const minValue = state.minValue;
  const maxValue = state.maxValue;

  // Check minValue
  if (minValue) {
    const firstDay = state.focusedDate.set({ year, month: 1, day: 1 });
    if (isBeforeMin(firstDay, minValue)) {
      return true;
    }
  }

  // Check maxValue
  if (maxValue) {
    const lastDay = state.focusedDate.set({ year, month: 12, day: 31 });
    if (isAfterMax(lastDay, maxValue)) {
      return true;
    }
  }

  // Check disabled ranges
  if (disablePresets.ranges && isYearRangeFullyDisabled(year, disablePresets.ranges, state)) {
    return true;
  }

  // Check future/past presets
  if (disablePresets.future || disablePresets.past) {
    return isYearDisabledByTimePreset(year, todayDate.year, disablePresets);
  }

  return false;
}
